if (!customElements.get('favorite-button')) {
  class FavoriteButton extends HTMLElement {
    constructor() {
      super();
      // Defer initialization to connectedCallback to ensure attributes are available.
    }

    connectedCallback() {
      // Check if already initialized to prevent re-binding events
      if (this.initialized) return;
      this.initialized = true;

      // Accessibility enhancements
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');

      this.productHandle = this.getAttribute('product-handle');
      this.favorites = this.getFavorites();

      this.addEventListener('click', this.toggleFavorite.bind(this));
      this.addEventListener('keydown', (event) => {
        // Trigger button click on Enter or Space key
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleFavorite(event);
        }
      });

      this.updateButtonState();
    }

    getFavorites() {
      const favorites = localStorage.getItem('favorites');
      if (!favorites) {
        return [];
      }
      try {
        const parsedFavorites = JSON.parse(favorites);
        if (Array.isArray(parsedFavorites)) {
          // Filter out any invalid, null, or non-string entries to self-heal the list.
          const cleanedFavorites = parsedFavorites.filter(handle => typeof handle === 'string' && handle.length > 0);
          
          // If the list was corrupted, update localStorage with the clean version immediately.
          if (cleanedFavorites.length !== parsedFavorites.length) {
            localStorage.setItem('favorites', JSON.stringify(cleanedFavorites));
          }
          return cleanedFavorites;
        }
        return []; // Return empty array if the stored value is not an array
      } catch (e) {
        console.error('Could not parse favorites from localStorage. Resetting.', e);
        localStorage.removeItem('favorites'); // Clear corrupted data
        return [];
      }
    }

    setFavorites(favorites) {
      localStorage.setItem('favorites', JSON.stringify(favorites));
    }

    toggleFavorite(event) {
      event.preventDefault();
      event.stopPropagation(); // Prevent clicks from bubbling up to parent links

      if (!this.productHandle) {
        console.error('Favorite button: product-handle attribute is missing.');
        return;
      }

      // Always re-fetch the latest array from storage
      let favorites = this.getFavorites();
      const index = favorites.indexOf(this.productHandle);

      if (index > -1) {
        favorites.splice(index, 1);
      } else {
        favorites.push(this.productHandle);
      }

      this.setFavorites(favorites);
      // Always re-fetch after setting
      this.favorites = this.getFavorites();
      this.updateButtonState();

      // Dispatch a custom event to notify other parts of the page (like the header count)
      console.log('[FavoriteButton] Dispatching favoritesUpdated event with:', this.favorites);
      document.dispatchEvent(new CustomEvent('favoritesUpdated', {
        bubbles: true,
        detail: { favorites: this.favorites, handle: this.productHandle }
      }));
      
      // Also dispatch the original event name for compatibility with existing code
      document.dispatchEvent(new CustomEvent('favorites-updated', {
        bubbles: true,
        detail: { favorites: this.favorites, handle: this.productHandle }
      }));
    }

    updateButtonState() {
      if (!this.productHandle) return;

      // Always re-fetch the latest array from storage
      this.favorites = this.getFavorites();

      // SVGs from snippets/icon-heart-filled.liquid and icon-heart-empty.liquid
      const svgFilled = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart" viewBox="0 0 24 24" width="22" height="22"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#e94b3c" stroke="#e94b3c" stroke-width="2"/></svg>`;
      const svgEmpty = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart" viewBox="0 0 24 24" width="22" height="22"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="#e94b3c" stroke-width="2"/></svg>`;

      if (this.favorites.includes(this.productHandle)) {
        this.classList.add('is-favorited');
        this.setAttribute('aria-label', 'Remove from Favorites');
        this.innerHTML = svgFilled;
      } else {
        this.classList.remove('is-favorited');
        this.setAttribute('aria-label', 'Add to Favorites');
        this.innerHTML = svgEmpty;
      }
    }
  }

  customElements.define('favorite-button', FavoriteButton);
}

